import { useState } from "react";
import { useLang } from "@/lib/adminLang";
import { kompakt, SCEN, STAGES, CATEGORIES, SPONSOR_MODES, monthLabel, prodLabel } from "./budsjettUtils";

export default function Scenarios({ data, actions }) {
  const { t } = useLang();
  const { scenarios, settings, computed } = data;
  const [cont, setCont] = useState({
    pct: settings.contingencyPct ?? 7.5,
    cats: settings.contingencyCategories || [],
  });
  const [probs, setProbs] = useState({ ...(settings.stageProbabilities || {}) });

  const rader = [
    [t("Physical sold"), (x) => `${x.physical.sold} (${x.physical.sellThroughPct}%)`],
    [t("In the room"), (x) => x.physical.attendance],
    [t("Digital passes"), (x) => x.digital.sold],
    [t("Dinner guests"), (x) => x.dinner.guests],
    [t("Partner revenue"), (x) => `${kompakt(x.revenue.sponsors)}${x.revenue.sponsorsBasis === "assumption" ? " *" : ""}`],
    [t("Revenue"), (x) => kompakt(x.revenue.total)],
    [t("Costs"), (x) => kompakt(x.costs.total)],
    [t("Margin"), (x) => `${x.margin}%`],
    [t("Break-even"), (x) => (x.breakEven.physicalTicketsRequired == null ? "—" : `${x.breakEven.physicalTicketsRequired} ${t("tickets")}`)],
    [t("Lowest cash"), (x) => (x.cash.lowest ? `${kompakt(x.cash.lowest.amount)} · ${monthLabel(x.cash.lowest.month)}` : "—")],
  ];

  return (
    <div data-testid="budget-scenarios" className="adm-rise">
      {/* 01 — Sammenligning på tvers, én rad per nøkkeltall */}
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-400">
        <span className="bud-mono font-normal mr-2 opacity-70">01</span>{t("Scenario comparison")}
      </p>
      <div className="overflow-x-auto adm-gruppe mt-3" data-testid="scenario-compare">
        <table className="adm-table adm-table-flat">
          <thead>
            <tr>
              <th className="w-[30%]">&nbsp;</th>
              {SCEN.map((sc) => (
                <th key={sc.k} className={`adm-right ${sc.k === "base" ? "!text-amber-600" : ""}`}>{sc.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rader.map(([label, fn]) => (
              <tr key={label} data-testid={`scenario-rad-${label}`}>
                <td className="text-neutral-500">{label}</td>
                {SCEN.map((sc) => (
                  <td key={sc.k} className={`text-right tabular-nums ${sc.k === "base" ? "font-semibold" : "text-neutral-500"}`}>
                    {fn(computed.scenarios[sc.k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr data-testid="scenario-compare-profit">
              <td>{t("Profit")}</td>
              {SCEN.map((sc) => {
                const x = computed.scenarios[sc.k];
                return (
                  <td key={sc.k} className={`text-right bud-num text-[17px] ${x.profit < 0 ? "text-red-600" : "text-emerald-700"}`}>
                    {kompakt(x.profit)}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-[10.5px] text-neutral-400 mt-2.5 max-w-[760px]">
        {t("* = planning assumption above real pipeline — reality wins once signed value exceeds it.")}
      </p>

      {/* 02 — Juster per scenario */}
      <p className="mt-12 text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-400">
        <span className="bud-mono font-normal mr-2 opacity-70">02</span>{t("Adjust per scenario")}
      </p>
      <div className="adm-delt grid-cols-1 lg:grid-cols-3 mt-3">
        {SCEN.map((sc) => {
          const scenDoc = scenarios.find((x) => x.key === sc.k) || {};
          return (
            <div key={sc.k} className="pb-4" data-testid={`scenario-card-${sc.k}`}>
              <p className={`bud-mono text-[10px] uppercase tracking-[0.2em] ${sc.k === "base" ? "text-amber-600" : "text-neutral-500"}`}>{sc.label}</p>
              <div className="mt-4 space-y-3">
                <label className="adm-label">
                  {t("Sponsor revenue counted as")}
                  <select
                    value={scenDoc.sponsorMode || "weighted"}
                    onChange={(e) => actions.patchScenario(sc.k, { sponsorMode: e.target.value })}
                    className="adm-input"
                    data-testid={`scenario-sponsormode-${sc.k}`}
                  >
                    {SPONSOR_MODES.map((m) => (
                      <option key={m.k} value={m.k}>{t(m.label)}</option>
                    ))}
                  </select>
                </label>
                <label className="adm-label">
                  {t("Production level")} ({prodLabel(scenDoc.productionFactor)})
                  <input
                    type="number"
                    step="0.05"
                    defaultValue={scenDoc.productionFactor ?? 1}
                    onBlur={(e) => Number(e.target.value) !== scenDoc.productionFactor && actions.patchScenario(sc.k, { productionFactor: Number(e.target.value) })}
                    className="adm-input"
                    data-testid={`scenario-production-${sc.k}`}
                  />
                </label>
                <label className="adm-label">
                  {t("Partner revenue assumption (NOK, blank = pipeline)")}
                  <input
                    type="number"
                    step="50000"
                    defaultValue={scenDoc.sponsorAssumption ?? ""}
                    onBlur={(e) => {
                      const v = e.target.value === "" ? null : Number(e.target.value);
                      if (v !== (scenDoc.sponsorAssumption ?? null)) actions.patchScenario(sc.k, { sponsorAssumption: v });
                    }}
                    className="adm-input"
                    data-testid={`scenario-sponsorassumption-${sc.k}`}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10.5px] text-neutral-400 mt-3 max-w-[760px]">
        {t("Attendance is driven by per-product sell-through (Revenue). Comps come from the inventory model. Individual lines can be overridden per scenario in the line editor.")}
      </p>

      {/* 03 — Reserve & sannsynligheter */}
      <div className="adm-delt grid-cols-1 lg:grid-cols-2 mt-12">
        {/* Contingency */}
        <div className="pb-4" data-testid="scenario-contingency">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-400 mb-3">{t("Contingency reserve")}</p>
          <div className="flex items-end gap-3">
            <label className="adm-label w-28">
              {t("Percent")}
              <input type="number" step="0.5" value={cont.pct} onChange={(e) => setCont((f) => ({ ...f, pct: e.target.value }))} className="adm-input" data-testid="contingency-pct-input" />
            </label>
            <button
              type="button"
              onClick={() => actions.patchSettings({ contingencyPct: Number(cont.pct) || 0, contingencyCategories: cont.cats })}
              className="adm-btn adm-btn-sm mb-0.5"
              data-testid="contingency-save"
            >
              {t("Save")}
            </button>
          </div>
          <p className="adm-label mt-4 mb-2">{t("Calculated on these categories")}</p>
          <div className="flex flex-wrap gap-x-2 gap-y-1">
            {CATEGORIES.map((c) => {
              const valgt = cont.cats.includes(c.k);
              return (
                <button
                  key={c.k}
                  type="button"
                  onClick={() => setCont((f) => ({ ...f, cats: valgt ? f.cats.filter((x) => x !== c.k) : [...f.cats, c.k] }))}
                  className={`bud-mono text-[10px] uppercase tracking-[0.14em] py-1 pr-3 text-left transition-colors ${
                    valgt ? "text-neutral-900" : "text-neutral-400 hover:text-neutral-700"
                  }`}
                  data-testid={`contingency-cat-${c.k}`}
                >
                  {valgt ? "[×]" : "[ ]"} {t(c.label)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Stage probabilities */}
        <div className="pb-4" data-testid="scenario-probs">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-400 mb-3">{t("Sponsor stages — default probability")}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {STAGES.map((st) => (
              <label key={st.k} className="adm-label">
                {t(st.label)}
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={probs[st.k] ?? 0}
                  onChange={(e) => setProbs((f) => ({ ...f, [st.k]: e.target.value }))}
                  className="adm-input"
                  data-testid={`prob-${st.k}-input`}
                />
              </label>
            ))}
          </div>
          <button
            type="button"
            onClick={() => actions.patchSettings({ stageProbabilities: Object.fromEntries(Object.entries(probs).map(([k, v]) => [k, Number(v) || 0])) })}
            className="adm-btn adm-btn-sm mt-4"
            data-testid="probs-save"
          >
            {t("Save probabilities")}
          </button>
          <p className="text-[10.5px] text-neutral-400 mt-3">{t("Can be overridden per sponsor in the sponsor editor.")}</p>
        </div>
      </div>
    </div>
  );
}
